import { useEffect, useState } from 'react';
import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, CopyableText, Menu, MenuItem, Stack } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiAppPlatform,
  IApiDownloadBundleExtension,
} from '~/lib/api/modules/Apps';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Table } from '~/components/molecules/Table';
import { PipelineListItem } from '~/components/organisms/PipelineListItem';
import { ITimeUnit } from '~/helpers/types';
import { useApi } from '~/lib/api';
import { useBuildsQuery } from '~/lib/api/modules/Builds/hooks/useBuildsQuery';

const GRID_TEMPLATE_COLUMNS = 'min-content 1fr';
const REFRESH_INTERVAL = 10 * ITimeUnit.SECOND;

export type IPipelineListProps = IContainerProps & {
  appId: string;
  platform: IApiAppPlatform;
};

export const PipelineList: React.FC<IPipelineListProps> = (props) => {
  const { sx, appId, platform } = props;
  const { t } = useTranslation();
  const api = useApi();
  const [building, setBuilding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const buildsQuery = useBuildsQuery(
    {
      appId: appId,
      platform,
      withHistory: true,
    },
    {
      refetchInterval: building ? REFRESH_INTERVAL : undefined,
    },
  );
  const build = buildsQuery.data?.[platform];
  const isPackageReady =
    buildsQuery.data?.[platform]?.pipeline?.current?.status === 'success';
  const isEmpty = !buildsQuery.isLoading && !build?.pipeline?.current;

  useEffect(
    () =>
      setBuilding(
        build?.pipeline?.current
          ? ['queued', 'starting', 'running'].includes(
              build.pipeline.current.status,
            )
          : false,
      ),
    [build],
  );

  const handleDownloadPackage = (
    extension: IApiDownloadBundleExtension,
  ): void => {
    setDownloading(true);
    void api.apps
      .downloadBundle(appId, {
        extension,
      })
      .then(({ url }) => {
        window.location.href = url;
      })
      .finally(() => setDownloading(false));
  };

  const handleDownloadScreenshots = (): void => {
    setDownloading(true);
    void api.apps
      .downloadScreenshots(appId)
      .then(({ url }) => {
        window.location.href = url;
      })
      .finally(() => setDownloading(false));
  };

  return (
    <ContentBlock sx={sx}>
      {buildsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={buildsQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('PipelineList.empty.title')}>
          <Button href={`/apps/${appId}/builds/${platform}/prepare`}>
            {t('PipelineList.empty.action-label')}
          </Button>
        </ContentBlock.Empty>
      ) : buildsQuery.isLoading ? (
        <ContentBlock.Body first>
          {/* TODO: use skeletons */}
          {t('common.loading')}
        </ContentBlock.Body>
      ) : build ? (
        <>
          <ContentBlock.Title
            supportingText={
              <>
                Package ID: <CopyableText>{build.build.packageId}</CopyableText>
              </>
            }
            action={
              <Stack horizontal gap={2}>
                <Menu
                  trigger={({ getProps }) => (
                    <Button
                      icon={<FontAwesomeIcon icon={faCaretDown} />}
                      trailingIcon
                      variant="text"
                      loading={downloading}
                      disabled={!isPackageReady}
                      {...getProps()}
                    >
                      {t('PipelineList.downloadMenu.label')}
                    </Button>
                  )}
                  placement="bottom-end"
                >
                  {platform === 'android' ? (
                    <>
                      <MenuItem
                        leadingIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
                        label={t('PipelineList.downloadMenu.items.aab.label')}
                        onClick={() => handleDownloadPackage('aab')}
                        supportingText={t(
                          'PipelineList.downloadMenu.items.aab.supportingText',
                        )}
                      />
                      <MenuItem
                        leadingIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
                        label={t('PipelineList.downloadMenu.items.apk.label')}
                        onClick={() => handleDownloadPackage('apk')}
                      />
                    </>
                  ) : (
                    <MenuItem
                      leadingIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
                      label={t('PipelineList.downloadMenu.items.ipa.label')}
                      onClick={() => handleDownloadPackage('ipa')}
                    />
                  )}
                  <MenuItem
                    leadingIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
                    label={t(
                      'PipelineList.downloadMenu.items.screenshots.label',
                    )}
                    onClick={() => handleDownloadScreenshots()}
                  />
                </Menu>
                <Button
                  href={`/apps/${appId}/builds/${platform}/prepare`}
                  loading={building}
                  loadingText={t('PipelineList.building')}
                >
                  {t('PipelineList.create')}
                </Button>
              </Stack>
            }
          >
            {build.build?.name}
          </ContentBlock.Title>

          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS} inFlow>
            <Table.Row first>
              <Table.Header>Status</Table.Header>
              <Table.Header>Pipeline</Table.Header>
            </Table.Row>
            {build.pipelines?.map((pipeline) => (
              <PipelineListItem
                key={pipeline._id}
                build={build.build}
                pipeline={pipeline}
              />
            ))}
          </Table>
        </>
      ) : null}
    </ContentBlock>
  );
};
